// MyProSidebar.tsx
import {
  DescriptionOutlined,
  NoteAltOutlined,
  RuleFolder,
  Search as SearchIcon,
  Explore,
  CloseOutlined,
  MenuOutlined
} from "@mui/icons-material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRightRounded";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  InputBase,
  Typography,
  alpha,
  keyframes,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Menu,
  MenuItem,
  MenuItemStyles,
  Sidebar,
  SubMenu,
  useProSidebar,
} from "react-pro-sidebar";
import { useLocation } from "react-router-dom";

import {
  BrainCog,
  Building,
  Building2,
  ClipboardList,
  CreditCard,
  Factory,
  FileCheck,
  Files,
  FileText,
  FolderOpen,
  House,
  IdCard,
  ListChevronsDownUp,
  LogOut,
  Package,
  Percent,
  Target,
  Receipt,
  ShoppingCart,
  Brain,
  User,
  UserCheck,
  UserPlus,
  Users,
} from "lucide-react";
import { useBranding } from "../../../BrandingContext";
import {
  CAN_ACCESS_APPLICATION,
  PRODUCT_CATEGORY,
  USER_ROLE_ENTITY_NAMES,
  USER_ROLES,
} from "../../../constants/applicationConstants";
import { useNavigation } from "../../../hooks/useNavigation";
import useToken from "../../../hooks/useToken";
import { fetchAgencyByUser } from "../../../redux/slice/agency/AgencySlice";
import { useCodetablesSlice } from "../../../redux/slice/codetableSlice";
import { canAccessIsrByEmail, isIsrAccessReady } from "../../../utils/isrAccess";
import {
  logout,
  setSelectedModule,
  useAuthSlice,
} from "../../../redux/slice/authSlice";
import { resetNotificationData } from "../../../redux/slice/inbox/createNotificationSlice";
import { getPracticesByUserRole } from "../../../redux/slice/practiceSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { tokens } from "../../../theme";
import { removeItemFromSessionStorage } from "../../../utils/StorageUtils";
import CustomTooltip from "../CustomTooltip";
import OptionsPopup from "../OptionsPopup";
import { useModalStyles } from "../useModalStyles";
import { useSidebarContext } from "./sidebarContext";

interface TourStep {
  key: string;
  title: string;
  description: string;
  token?: string;
  requiresIsrEmailAccess?: boolean;
}

const TOUR_STEPS: TourStep[] = [
  { key: "home",             title: "Home",             description: "Your dashboard hub. View KPIs, analytics, quick actions, and notifications at a glance.", token: CAN_ACCESS_APPLICATION.DASHBOARD },
  { key: "patient",          title: "Patient",          description: "Manage your patient records, view patient details, and track treatment progress.",       token: CAN_ACCESS_APPLICATION.PATIENT },
  { key: "prescription",     title: "Rx Request",      description: "View and manage prescriptions for your patients.",                                       token: CAN_ACCESS_APPLICATION.PRESCRIPTION_MANAGEMENT },
  { key: "ivr",              title: "IVR",              description: "Submit and manage Insurance Verification Requests for your patients.",                   token: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT },
  { key: "order",            title: "Order",            description: "Place and track product orders for patient treatments.",                                  token: CAN_ACCESS_APPLICATION.ORDER_MANAGEMENT },
  { key: "product",          title: "Product",          description: "View and manage all your products in one place.",                                        token: CAN_ACCESS_APPLICATION.PRODUCTS },
  { key: "isr",              title: "ISR",              description: "Create and manage sales goals and ISR config.",                                          requiresIsrEmailAccess: true },
  { key: "claim",            title: "Claim",            description: "Submit and track insurance or reimbursement claims.",                                    token: CAN_ACCESS_APPLICATION.CLAIM_MANAGEMENT },
  { key: "invoice",          title: "Invoice",          description: "Manage and pay billing invoices.",                                                       token: CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT },
  { key: "payment",          title: "Payment",          description: "Track payments and manage your payment history.",                                        token: CAN_ACCESS_APPLICATION.PAYMENT_MANAGEMENT },
  { key: "commission",       title: "FinanceHQ",       description: "View your service fee statements and earnings.",                                           token: CAN_ACCESS_APPLICATION.COMMISSION_MANAGEMENT },
  { key: "agreement",        title: "Agreement",        description: "Manage and store relevant agreements",                                                   token: CAN_ACCESS_APPLICATION.PRODUCT_AGREEMENT },
  { key: "templates",        title: "Templates",        description: "Manage template details.",                                                               token: CAN_ACCESS_APPLICATION.TEMPLATES },
  { key: "user",             title: "User",             description: "Manage user accounts and permissions for your organization.",                            token: CAN_ACCESS_APPLICATION.USER_MANAGEMENT },
  { key: "user_create",      title: "User Create",      description: "Create new user accounts for your organization.",                                        token: CAN_ACCESS_APPLICATION.USER_CREATE },
  { key: "practice",         title: "Practice",         description: "Manage your practice information: providers, locations, etc.",                           token: CAN_ACCESS_APPLICATION.PRACTICE },
  { key: "provider",         title: "Provider",         description: "Manage healthcare providers associated with your practice.",                             token: CAN_ACCESS_APPLICATION.PROVIDER_MANAGEMENT },
  { key: "agency",           title: "Agency",           description: "View and manage agency partnerships.",                                                   token: CAN_ACCESS_APPLICATION.AGENCY_MANAGEMENT },
  { key: "manufacturer",     title: "Manufacturer",     description: "Access manufacturer information and contacts.",                                          token: CAN_ACCESS_APPLICATION.MANUFACTURER_MANAGEMENT },
  { key: "ai_medical_audit", title: "Medical Note",     description: "View and manage AI medical note audit records.",                                         token: CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT },
  { key: "faceSheet",        title: "Facesheet",        description: "Monitor facesheet extraction records and patient creation status.",                        token: CAN_ACCESS_APPLICATION.FACESHEET },
  { key: "document",         title: "Resources",        description: "Access and manage uploaded resources and files.",                                        token: CAN_ACCESS_APPLICATION.DOCUMENT_MANAGEMENT },
  { key: "sign_out",         title: "Sign Out",         description: "Securely sign out of your account." },
];

const TOUR_PARENT_MAP: Record<string, string> = {
  invoice:      "Finance",
  payment:      "Finance",
  commission:   "Finance",
  isr:          "Finance",
  user:         "Your Org",
  user_create:  "Your Org",
  practice:     "Your Org",
  provider:     "Your Org",
  agency:       "Your Org",
  manufacturer: "Your Org",
  ai_medical_audit: "AI Audit",
  faceSheet:        "AI Audit",
};

const tooltipEnter = keyframes`
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
`;
const tooltipExit = keyframes`
  from { opacity: 1; transform: translateX(0); }
  to   { opacity: 0; transform: translateX(-10px); }
`;

const TOOLTIP_WIDTH = 310;
const TOOLTIP_GAP = 14;
const FOOTER_CLEAR = 70;
const MOBILE_STEPPER_HEIGHT = 110;
const SHOW_SIDEBAR_SEARCH = false;

export type SideBarLeafItem = {
  type: "item";
  title: string;
  to: string;
  icon: JSX.Element;
  token?: string;
  requiresIsrEmailAccess?: boolean;
  tourKey?: string;
  description?: string;
};

type SideBarGroup = {
  type: "group";
  title: string;
  icon: JSX.Element;
  children: SideBarLeafItem[];
};

export const sideBarTree: Array<SideBarLeafItem | SideBarGroup> = [
  { type: "item", title: "Home",    to: "/dashboard",               icon: <House size={18} />,         token: CAN_ACCESS_APPLICATION.DASHBOARD,        tourKey: "home" },
  { type: "item", title: "Patient", to: "/patients",                icon: <Users size={18} />,         token: CAN_ACCESS_APPLICATION.PATIENT,          tourKey: "patient" },
  { type: "item", title: "Rx Request", to: "/prescription_list",   icon: <ClipboardList size={18} />, token: CAN_ACCESS_APPLICATION.PRESCRIPTION_MANAGEMENT, tourKey: "prescription" },
  { type: "item", title: "IVR",     to: "/ivr_list",                icon: <RuleFolder />,              token: CAN_ACCESS_APPLICATION.IVR_MANAGEMENT,   tourKey: "ivr" },
  { type: "item", title: "Order",   to: "/order_list",              icon: <ShoppingCart size={16} />,  token: CAN_ACCESS_APPLICATION.ORDER_MANAGEMENT, tourKey: "order" },
  { type: "item", title: "Product", to: "/products/explore_market", icon: <Package size={18} />,       token: CAN_ACCESS_APPLICATION.PRODUCTS,         tourKey: "product" },
  { type: "item", title: "Claim",   to: "/claim_list",              icon: <NoteAltOutlined />,         token: CAN_ACCESS_APPLICATION.CLAIM_MANAGEMENT, tourKey: "claim" },
  {
    type: "group", title: "Finance", icon: <Receipt size={18} />,
    children: [
      { type: "item", title: "Invoice",    to: "/invoice_list",    icon: <FileCheck size={18} />,  token: CAN_ACCESS_APPLICATION.INVOICE_MANAGEMENT,    tourKey: "invoice" },
      { type: "item", title: "Payment",    to: "/payment_list",    icon: <CreditCard size={18} />, token: CAN_ACCESS_APPLICATION.PAYMENT_MANAGEMENT,    tourKey: "payment" },
      { type: "item", title: "FinanceHQ", to: "/commission_list", icon: <Percent size={18} />,    token: CAN_ACCESS_APPLICATION.COMMISSION_MANAGEMENT, tourKey: "commission" },
      { type: "item", title: "ISR",             to: "/isr",          icon: <Target size={18} />,    requiresIsrEmailAccess: true,                        tourKey: "isr" },
    ],
  },
  {
    type: "group", title: "Agreement", icon: <FileText size={18} />,
    children: [
      { type: "item", title: "Product Agreement",      to: "/practice_product_agreement",       icon: <FileText size={18} />,     token: CAN_ACCESS_APPLICATION.PRODUCT_AGREEMENT,              tourKey: "product_agreement" },
      { type: "item", title: "BAA",                    to: "/baa_Detail",                       icon: <Files size={18} />,        token: CAN_ACCESS_APPLICATION.BAA_MANAGEMENT,                 tourKey: "baa" },
      { type: "item", title: "Medical Note Agreement", to: "/service_subscription_list",        icon: <ClipboardList size={18} />,token: CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT,             tourKey: "medical_note_agreement" },
      { type: "item", title: "Agreement Request",      to: "/products/agreement_request_list",  icon: <FileText size={18} />,     token: CAN_ACCESS_APPLICATION.AGREEMENT_REQUEST_MANAGEMENT,   tourKey: "agreement_request" },
    ],
  },
  { type: "item", title: "Templates", to: "/template_list", icon: <DescriptionOutlined />, token: CAN_ACCESS_APPLICATION.TEMPLATES, tourKey: "templates" },
  {
    type: "group", title: "Your Org", icon: <Building size={18} />,
    children: [
      { type: "item", title: "User",         to: "/user_list",         icon: <User size={18} />,      token: CAN_ACCESS_APPLICATION.USER_MANAGEMENT,         tourKey: "user" },
      { type: "item", title: "User Create",  to: "/form",              icon: <UserPlus size={18} />,  token: CAN_ACCESS_APPLICATION.USER_CREATE,             tourKey: "user_create" },
      { type: "item", title: "Practice",     to: "/practice_list",     icon: <Building size={18} />,  token: CAN_ACCESS_APPLICATION.PRACTICE,                tourKey: "practice" },
      { type: "item", title: "Provider",     to: "/provider_list",     icon: <UserCheck size={18} />, token: CAN_ACCESS_APPLICATION.PROVIDER_MANAGEMENT,     tourKey: "provider" },
      { type: "item", title: "Agency",       to: "/agency_list",       icon: <Building2 size={16} />, token: CAN_ACCESS_APPLICATION.AGENCY_MANAGEMENT,       tourKey: "agency" },
      { type: "item", title: "Manufacturer", to: "/manufacturer_list", icon: <Factory size={18} />,   token: CAN_ACCESS_APPLICATION.MANUFACTURER_MANAGEMENT, tourKey: "manufacturer" },
    ],
  },
  // CS - 1790
  {
    type: "group", title: "AI Audit", icon: <BrainCog size={18} />,
    children: [
      { type: "item", title: "Medical Note", to: "/ai_medical_audit", icon: <Brain size={18} />, token: CAN_ACCESS_APPLICATION.MEDICAL_NOTE_AUDIT, tourKey: "ai_medical_audit" },
      { type: "item", title: "Facesheet",    to: "/faceSheet",        icon: <IdCard size={18} />,      token: CAN_ACCESS_APPLICATION.FACESHEET,          tourKey: "faceSheet" },
    ],
  },
];

export const bottomItems: SideBarLeafItem[] = [
  { type: "item", title: "Resources", to: "/resource_list", icon: <FolderOpen size={18} />, token: CAN_ACCESS_APPLICATION.DOCUMENT_MANAGEMENT, tourKey: "resources" },
];

export const sideBarItems: SideBarLeafItem[] = [
  ...sideBarTree.flatMap((n) => (n.type === "item" ? [n] : n.children)),
  ...bottomItems,
];

function SpotlightOverlay({ activeEl }: { activeEl: HTMLElement | null }) {
  const [rect, setRect] = useState({ top: 0, left: 0, width: 0, height: 0 });

  const update = useCallback(() => {
    if (!activeEl) return;
    const r = activeEl.getBoundingClientRect();
    const pad = 5;
    setRect({ top: r.top - pad, left: r.left - pad, width: r.width + pad * 2, height: r.height + pad * 2 });
  }, [activeEl]);

  useEffect(() => {
    const ids = [setTimeout(update, 60), setTimeout(update, 300), setTimeout(update, 550)];
    window.addEventListener("resize", update);
    return () => { ids.forEach(clearTimeout); window.removeEventListener("resize", update); };
  }, [update]);

  return (
    <Box sx={{
      position: "fixed",
      top: rect.top, left: rect.left, width: rect.width, height: rect.height,
      borderRadius: "10px",
      zIndex: 1400,
      pointerEvents: "none",
      boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
      transition: "top 0.4s cubic-bezier(0.22,1,0.36,1), left 0.4s cubic-bezier(0.22,1,0.36,1), width 0.4s ease, height 0.4s ease",
    }} />
  );
}

const Item = ({
  title, to, icon, selected, setSelected,
  broken, toggleSidebar, collapsed,
  isSubmenuItem = false, onSubMenuItemClick,
  isTourHighlighted = false,
  registerTourRef, disabled = false,
}: {
  title: string; to: string; icon: JSX.Element;
  selected: string; setSelected: (s: string) => void;
  broken: boolean; toggleSidebar: () => void; collapsed: boolean;
  isSubmenuItem?: boolean; onSubMenuItemClick?: () => void;
  isTourHighlighted?: boolean;
  registerTourRef?: (el: HTMLElement | null) => void;
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { navigate } = useNavigation();
  const { userInfo } = useAuthSlice();
  const dispatch = useAppDispatch();
  const isSelected = selected === title;

  const handleClick = async () => {
    if (disabled) return;
    setSelected(title);
    if (title === "Practice" && userInfo.entityName === USER_ROLE_ENTITY_NAMES.PRACTICE) {
      dispatch(getPracticesByUserRole(false)).then((res: any) => {
        res?.payload?.length === 1 && userInfo.userRole !== USER_ROLES.ROLE_PRACTICE_USER_PRU
          ? navigate("/practice_edit_view", { state: { practiceId: res.payload[0].practiceId } })
          : navigate("/practice_list");
      });
    } else if (title === "Agency" && (userInfo.entityName === USER_ROLE_ENTITY_NAMES.AGENCY || userInfo.entityName === USER_ROLE_ENTITY_NAMES.ACCOUNTEXECUTIVE)) {
      dispatch(fetchAgencyByUser()).then((res: any) => {
        if (res?.payload?.code === "V3OK" && res?.payload?.message === "SUCCESS")
          navigate("/agency_form", { state: { agency: { agencyId: res.payload.data.agencyId } } });
      });
    } else if (title === PRODUCT_CATEGORY.CONSERVATIVE) {
      window.open(process.env.REACT_APP_HEALINGBIOLOGIX_URL, "_blank");
    } else {
      navigate(to);
    }
    if (collapsed && isSubmenuItem && onSubMenuItemClick) onSubMenuItemClick();
    if (broken) toggleSidebar();
  };

  const menuItem = (
    <Box
      ref={(el: HTMLDivElement | null) => registerTourRef?.(el)}
      sx={{
        borderRadius: "10px",
        border: isTourHighlighted
          ? "1px solid rgba(20,184,166,0.8)"
          : isSelected
            ? "1px solid rgba(20,184,166,0.32)"
            : "1px solid transparent",
        background: isTourHighlighted
          ? alpha("#14b8a6", 0.12)
          : isSelected
            ? theme.palette.mode === "dark"
              ? "linear-gradient(135deg, rgba(13,148,136,0.28) 0%, rgba(15,118,110,0.18) 50%, rgba(13,148,136,0.10) 100%)"
              : "linear-gradient(160deg, rgba(13,148,136,0.1) 0%, rgba(22,163,74,0.3) 100%)"
            : "transparent",
        boxShadow: isSelected && !isTourHighlighted
          ? "0 2px 14px rgba(20,184,166,0.18), inset 0 1px 0 rgba(20,184,166,0.12)"
          : "none",
        position: "relative",
        zIndex: isTourHighlighted ? 1450 : "auto",
        transition: "border-color 0.25s, background 0.25s, box-shadow 0.25s",
        mx: isTourHighlighted ? "-2px" : 0,
        px: isTourHighlighted ? "2px" : 0,
      }}
    >
      <MenuItem
        disabled={disabled}
        style={{
          backgroundColor: "transparent",
          borderRadius: 10,
        }}
        onClick={handleClick}
        icon={icon}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: isSelected ? 700 : 500,
            color: isSelected ? "#14b8a6" : "inherit",
            whiteSpace: "normal",
            wordBreak: "break-word",
            lineHeight: 1.25,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            ...(collapsed && isSubmenuItem ? { letterSpacing: "0.01em" } : {}),
          }}
        >
          {title}
        </Typography>
      </MenuItem>
    </Box>
  );

  if (isSubmenuItem) return menuItem;
  return (
    <CustomTooltip title={collapsed ? title : ""} placement="right" arrow>
      {menuItem}
    </CustomTooltip>
  );
};

const MyProSidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { navigate } = useNavigation();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { sidebarRTL, sidebarImage, setIsTourActive } = useSidebarContext();
  const { collapseSidebar, toggleSidebar, collapsed, broken } = useProSidebar();
  const { isDark, tokens: modalTokens } = useModalStyles();
  const { bg: modalBg, border: modalBorder, textPrimary: modalTextPrimary, accent: modalAccent } = modalTokens;

  /** Collapsed flyout: same surface tokens as dashboard modals (`useModalStyles` / Dialog paper). */
  const sidebarMenuItemStyles = useMemo((): MenuItemStyles => {
    const c = tokens(theme.palette.mode);
    const isCollapsedFlyoutChild = (level: number) => collapsed && level > 0;
    const flyoutShadow = isDark
      ? "0 32px 80px rgba(0,0,0,0.5)"
      : "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)";

    return {
      root: { fontSize: "14px", fontWeight: 600 },
      button: (params) =>
        isCollapsedFlyoutChild(params.level)
          ? {
              borderRadius: "10px",
              color: modalTextPrimary,
              transition:
                "background-color 0.22s cubic-bezier(0.4, 0, 0.2, 1), color 0.22s cubic-bezier(0.4, 0, 0.2, 1), transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.22s ease",
              boxShadow: "inset 3px 0 0 0 transparent",
              "&:hover": {
                backgroundColor: isDark ? "rgba(255,255,255,0.07)" : "rgba(20,184,166,0.09)",
                color: `${modalAccent} !important`,
                transform: "translateX(3px)",
                boxShadow: "inset 3px 0 0 0 rgba(20,184,166,0.75)",
              },
              "&:active": {
                backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.14)",
                transform: "translateX(1px)",
                transitionDuration: "0.12s",
              },
              "& .ps-menu-icon": {
                transition: "color 0.22s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
              },
              "&:hover .ps-menu-icon": {
                color: modalAccent,
                transform: "scale(1.08)",
              },
              "& .ps-menu-icon svg": {
                transition: "color 0.22s ease, transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
              },
              "& .ps-menu-label .MuiTypography-root": {
                transition:
                  "color 0.22s cubic-bezier(0.4, 0, 0.2, 1), letter-spacing 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
              },
              "&:hover .ps-menu-label .MuiTypography-root": {
                color: `${modalAccent}`,
                letterSpacing: "0.045em",
              },
            }
          : {
              "&:hover": {
                backgroundColor: "transparent",
                color: (theme.palette.mode === "dark" ? c.warning[200] : c.success[300]) + "!important",
              },
            },
      icon: (params) =>
        isCollapsedFlyoutChild(params.level)
          ? {
              color: modalTextPrimary,
              "& svg": { color: modalTextPrimary, stroke: "currentColor" },
            }
          : undefined,
      label: (params) => ({
        paddingLeft: 0,
        ...(isCollapsedFlyoutChild(params.level)
          ? { color: modalTextPrimary, fontWeight: 600, transition: "color 0.22s ease" }
          : {}),
      }),
      subMenuContent: collapsed
        ? {
            backgroundColor: modalBg,
            color: modalTextPrimary,
            border: `1px solid ${modalBorder}`,
            borderRadius: "16px",
            minWidth: 180,
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(0.5),
            boxShadow: flyoutShadow,
            fontWeight: 600,
          }
        : { backgroundColor: c.grey[800] },
    };
  }, [collapsed, isDark, modalAccent, modalBg, modalBorder, modalTextPrimary, theme]);

  const isTokenValueAvailable = useToken();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { selectedModule, userInfo } = useAuthSlice();
  const { allCodeTablesValuesMap, allCodeTableValueFetchStatus } = useCodetablesSlice();
  const canAccessIsr = canAccessIsrByEmail(allCodeTablesValuesMap, userInfo?.userName);
  const isrAccessReady = isIsrAccessReady(allCodeTablesValuesMap, allCodeTableValueFetchStatus);
  const stayOnIsrWhilePending = !isrAccessReady && location.pathname.startsWith("/isr");
  const activeTourSteps = TOUR_STEPS.filter(
    (s) => s.requiresIsrEmailAccess ? canAccessIsr : (!s.token || isTokenValueAvailable(s.token))
  );
  const branding = useBranding();

  const [openSubMenus, setOpenSubMenus] = useState<Record<string, boolean>>({});
  const [showInfoPopup, setShowInfoPopup] = useState({ message: "", show: false });
  const [tourActive, setTourActive] = useState(false);
  const setTourActiveAndSync = (val: boolean) => { setTourActive(val); setIsTourActive(val); };
  const [tourStep, setTourStep] = useState(0);
  const [tooltipAnim, setTooltipAnim] = useState<"enter" | "exit">("enter");
  const [animKey, setAnimKey] = useState(0);
  const [tooltipTop, setTooltipTop] = useState(100);
  const [sidebarRight, setSidebarRight] = useState(240);

  const [search, setSearch] = useState("");
  const [searchHighlightIdx, setSearchHighlightIdx] = useState(0);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const searchResultRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    const el = searchResultRefs.current[searchHighlightIdx];
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [searchHighlightIdx]);

  useEffect(() => {
    if (collapsed) setSearch("");
  }, [collapsed]);

  useEffect(() => {
    setSearchHighlightIdx(0);
    searchResultRefs.current = [];
  }, [search]);

  useEffect(() => { setSearchHighlightIdx(0); }, [search]);

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return null;
    const results: Array<{ item: SideBarLeafItem; groupLabel?: string }> = [];
    sideBarTree.forEach((node) => {
      if (node.type === "item") {
        if ((node.requiresIsrEmailAccess ? canAccessIsr : isTokenValueAvailable(node.token)) && node.title.toLowerCase().includes(q)) {
          results.push({ item: node });
        }
      } else {
        const groupMatch = node.title.toLowerCase().includes(q);
        node.children.forEach((child) => {
          const canSeeChild = child.requiresIsrEmailAccess
            ? canAccessIsr
            : isTokenValueAvailable(child.token);
          if (canSeeChild && (child.title.toLowerCase().includes(q) || groupMatch)) {
            results.push({ item: child, groupLabel: node.title });
          }
        });
      }
    });
    bottomItems.forEach((b) => {
      if (isTokenValueAvailable(b.token) && b.title.toLowerCase().includes(q)) {
        results.push({ item: b });
      }
    });
    return results;
  }, [search, isTokenValueAvailable, canAccessIsr]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!filteredItems || filteredItems.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSearchHighlightIdx((i) => Math.min(i + 1, filteredItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSearchHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = filteredItems[searchHighlightIdx];
      if (hit) {
        setSelected(hit.item.title);
        setSearch("");
        navigate(hit.item.to);
      }
    } else if (e.key === "Escape") {
      setSearch("");
      searchRef.current?.blur();
    }
  };

  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const sidebarBoxRef = useRef<HTMLDivElement | null>(null);

  const currentTourStep = activeTourSteps[tourStep] ?? null;
  const tourRequiredGroup = currentTourStep ? TOUR_PARENT_MAP[currentTourStep.key] : undefined;

  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveOpenSubMenus = useMemo(() => {
    if (!tourActive || !tourRequiredGroup) return openSubMenus;
    return { ...openSubMenus, [tourRequiredGroup]: true };
  }, [tourActive, tourRequiredGroup, openSubMenus]);

  const menuKey = `${collapsed}-${location.pathname}`;

  const updateTooltipPosition = useCallback(() => {
    if (!currentTourStep) return;
    const el = itemRefs.current[currentTourStep.key];
    if (!el) return;

    el.scrollIntoView({ behavior: "smooth", block: "nearest" });

    requestAnimationFrame(() => {
      if (sidebarBoxRef.current) {
        setSidebarRight(sidebarBoxRef.current.getBoundingClientRect().right);
      }

      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const tooltipH = tooltipRef.current?.offsetHeight ?? 220;
      const bottomClear = broken ? MOBILE_STEPPER_HEIGHT + 8 : FOOTER_CLEAR;
      let top = r.top - 8;
      top = Math.max(12, top);
      top = Math.min(top, vh - tooltipH - bottomClear);
      setTooltipTop(top);
    });
  }, [currentTourStep, broken]);

  useEffect(() => {
    if (!tourActive) return;
    const ids = [
      setTimeout(updateTooltipPosition, 80),
      setTimeout(updateTooltipPosition, 350),
      setTimeout(updateTooltipPosition, 550),
    ];
    return () => ids.forEach(clearTimeout);
  }, [tourStep, tourActive, effectiveOpenSubMenus, updateTooltipPosition]);

  useEffect(() => {
    window.addEventListener("resize", updateTooltipPosition);
    return () => window.removeEventListener("resize", updateTooltipPosition);
  }, [updateTooltipPosition]);

  const changeStep = (fn: () => void) => {
    setTooltipAnim("exit");
    setTimeout(() => { fn(); setAnimKey((k) => k + 1); setTooltipAnim("enter"); }, 180);
  };

  const tourSeenKey = userInfo?.userId ? `tour_seen_${userInfo.userId}` : null;
  const wasCollapsedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!tourSeenKey) return;
    if (localStorage.getItem(tourSeenKey)) return;
    const id = setTimeout(() => {
      wasCollapsedRef.current = collapsed;
      if (collapsed) collapseSidebar(false);
      if (isMobile) toggleSidebar();
      setTourStep(0);
      setTourActiveAndSync(true);
    }, 600);
    return () => clearTimeout(id);
  }, [tourSeenKey]);

  const markTourSeen = () => { if (tourSeenKey) localStorage.setItem(tourSeenKey, "1"); };

  const startTour = () => {
    wasCollapsedRef.current = collapsed;
    if (collapsed) collapseSidebar(false);
    setTourStep(0);
    setTourActiveAndSync(true);
  };

  const endTour = () => {
    markTourSeen();
    setTourActiveAndSync(false);
    if (!broken) collapseSidebar(true);
  };

  const handleTourNext = () =>
    tourStep < activeTourSteps.length - 1
      ? changeStep(() => setTourStep((s) => s + 1))
      : endTour();
  const handleTourBack = () => tourStep > 0 && changeStep(() => setTourStep((s) => s - 1));
  const handleTourSkip = () => endTour();

  const isTourHighlighted = (key?: string) =>
    tourActive && !!currentTourStep && currentTourStep.key === key;

  const setSelected = (s: string) => dispatch(setSelectedModule(s));

  useEffect(() => {
    removeItemFromSessionStorage("searchCriterias");
    if (!collapsed && !localStorage.getItem(`tour_seen_${userInfo?.userId}`)) return;
    if (!collapsed) collapseSidebar();
  }, []);

  useEffect(() => () => removeItemFromSessionStorage("searchCriterias"), []);

  useEffect(() => {
    if (location.pathname === "/error" || location.pathname === "/restricted") return;

    const match = sideBarItems.find(
      (it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/")
    );

    if (match) {
      if (match.requiresIsrEmailAccess && !isrAccessReady) {
        return;
      }
      if (match.requiresIsrEmailAccess ? canAccessIsr : isTokenValueAvailable(match.token)) {
        dispatch(setSelectedModule(match.title));
        const parentGroup = sideBarTree.find(
          (node): node is SideBarGroup =>
            node.type === "group" &&
            node.children.some((c) => c.title === match.title)
        );

        setOpenSubMenus(parentGroup ? { [parentGroup.title]: true } : {});
      } else {
        navigate("/");
        setOpenSubMenus({});
      }
    } else {
      setOpenSubMenus({});
    }
  }, [location.pathname, canAccessIsr, isrAccessReady]);

  const handleGroupMouseEnter = (title: string) => {
    hoverTimerRef.current = setTimeout(() => setHoveredGroup(title), 100);
  };

  const handleGroupMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    setHoveredGroup(null);
  };

  useEffect(() => {
    if (!hoveredGroup) return;
    const handleMove = (e: MouseEvent) => {
      const sidebarWidth = sidebarBoxRef.current?.getBoundingClientRect().right ?? 240;
      if (e.clientX > sidebarWidth - 10) setHoveredGroup(null);
    };
    document.addEventListener("mousemove", handleMove);
    return () => document.removeEventListener("mousemove", handleMove);
  }, [hoveredGroup]);

  useEffect(() => {
    return () => { if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current); };
  }, []);

  const renderSearchResults = () => {
    if (!filteredItems) return null;
    if (filteredItems.length === 0) {
      return (
        <Box py={4} px={2} display="flex" flexDirection="column" alignItems="center" gap={1}>
          <SearchIcon sx={{ fontSize: 28, color: "#374151" }} />
          <Typography variant="caption" sx={{ color: "#4b5563", textAlign: "center" }}>
            No results for "{search}"
          </Typography>
        </Box>
      );
    }

    let lastGroup: string | undefined = undefined;
    return (
      <>
        {filteredItems.map(({ item, groupLabel }, idx) => {
          const showGroupHeader = groupLabel && groupLabel !== lastGroup;
          lastGroup = groupLabel;
          const isKeyboardActive = idx === searchHighlightIdx;
          return (
            <Box key={idx}>
              {showGroupHeader && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    px: 1.5,
                    pt: idx === 0 ? 0.5 : 1.5,
                    pb: 0.25,
                    color: "#4b5563",
                    fontWeight: 700,
                    fontSize: "0.68rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                  }}
                >
                  {groupLabel}
                </Typography>
              )}
              <Box
                key={idx}
                ref={(el: HTMLDivElement | null) => { searchResultRefs.current[idx] = el; }}
                onMouseEnter={() => setSearchHighlightIdx(idx)}
                sx={{
                  borderRadius: "10px",
                  outline: isKeyboardActive
                    ? `1px solid ${alpha("#14b8a6", 0.45)}`
                    : "1px solid transparent",
                  background: isKeyboardActive
                    ? "linear-gradient(135deg, rgba(13,148,136,0.22) 0%, rgba(15,118,110,0.12) 100%)"
                    : "transparent",
                  boxShadow: isKeyboardActive ? "0 2px 10px rgba(20,184,166,0.14)" : "none",
                  transition: "all 0.15s ease",
                  position: "relative",
                  "& .ps-menu-label": {
                    maxWidth: isKeyboardActive ? "calc(100% - 36px)" : "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <Item
                  title={item.title}
                  to={item.to}
                  icon={item.icon}
                  selected={isKeyboardActive ? item.title : selectedModule}
                  setSelected={(s) => { setSelected(s); setSearch(""); }}
                  broken={broken}
                  toggleSidebar={toggleSidebar}
                  collapsed={collapsed}
                  isTourHighlighted={false}
                  disabled={tourActive}
                />
                {isKeyboardActive && (
                  <Box
                    sx={{
                      position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                      display: "flex", alignItems: "center", gap: 0.4, pointerEvents: "none",
                    }}
                  >
                    <Box
                      component="kbd"
                      sx={{
                        fontSize: "0.62rem", fontFamily: "monospace",
                        px: 0.6, py: 0.2, borderRadius: "4px",
                        border: "1px solid rgba(20,184,166,0.35)",
                        color: "#14b8a6",
                        backgroundColor: alpha("#14b8a6", 0.08),
                        lineHeight: 1.4,
                      }}
                    >
                      ↵
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  const renderTree = () =>
    sideBarTree.map((node, idx) => {
      if (node.type === "item") {
        if (node.requiresIsrEmailAccess ? (!canAccessIsr && !stayOnIsrWhilePending) : !isTokenValueAvailable(node.token)) return null;
        return (
          <Item
            key={idx}
            title={node.title} to={node.to} icon={node.icon}
            selected={selectedModule} setSelected={setSelected}
            broken={broken} toggleSidebar={toggleSidebar} collapsed={collapsed}
            isTourHighlighted={isTourHighlighted(node.tourKey)}
            registerTourRef={node.tourKey ? (el) => { itemRefs.current[node.tourKey!] = el; } : undefined}
            disabled={tourActive}
          />
        );
      }

      const visibleChildren = node.children.filter((c) => (
        c.requiresIsrEmailAccess
          ? (canAccessIsr || stayOnIsrWhilePending)
          : isTokenValueAvailable(c.token)
      ));
      if (visibleChildren.length === 0) return null;

      if (node.title === "Agreement") {
        return (
          <CustomTooltip key={idx} title={collapsed ? node.title : ""} placement="right" arrow disableHoverListener open={collapsed && hoveredGroup === node.title}>
            <Box
              onMouseEnter={() => handleGroupMouseEnter(node.title)}
              onMouseLeave={handleGroupMouseLeave}
              ref={(el: HTMLDivElement | null) => { itemRefs.current["agreement"] = el; }}
              sx={{
                borderRadius: "6px",
                backgroundColor: isTourHighlighted("agreement") ? alpha("#14b8a6", 0.12) : "transparent",
                position: "relative",
                zIndex: isTourHighlighted("agreement") ? 1450 : "auto",
                transition: "border-color 0.25s, background-color 0.25s",
                mx: isTourHighlighted("agreement") ? "-2px" : 0,
                px: isTourHighlighted("agreement") ? "2px" : 0,
              }}
            >
              <SubMenu
                icon={node.icon}
                label={<Typography style={{ fontSize: "14px", fontWeight: 600 }}>{node.title}</Typography>}
                open={!!effectiveOpenSubMenus[node.title]}
                disabled={tourActive}
                onOpenChange={(open) => {
                  if (tourActive) return;
                  setOpenSubMenus(prev => ({ ...prev, [node.title]: open }));
                }}
              >
                {visibleChildren.map((child, cIdx) => (
                  <Item
                    key={`${idx}-${cIdx}`}
                    title={child.title} to={child.to} icon={child.icon}
                    selected={selectedModule} setSelected={setSelected}
                    broken={broken} toggleSidebar={toggleSidebar} collapsed={collapsed}
                    isSubmenuItem onSubMenuItemClick={() => setOpenSubMenus({})}
                    isTourHighlighted={false}
                    registerTourRef={undefined}
                    disabled={tourActive}
                  />
                ))}
              </SubMenu>
            </Box>
          </CustomTooltip>
        );
      }

      return (
        <CustomTooltip key={idx} title={collapsed ? node.title : ""} placement="right" arrow disableHoverListener open={collapsed && hoveredGroup === node.title}>
          <Box
            onMouseEnter={() => handleGroupMouseEnter(node.title)}
            onMouseLeave={handleGroupMouseLeave}
          >
            <SubMenu
              key={idx}
              icon={node.icon}
              label={<Typography style={{ fontSize: "14px", fontWeight: 600 }}>{node.title}</Typography>}
              open={!!effectiveOpenSubMenus[node.title]}
              disabled={tourActive}
              onOpenChange={(open) => {
                if (tourActive) return;
                setOpenSubMenus(prev => ({ ...prev, [node.title]: open }));
              }}
            >
              {visibleChildren.map((child, cIdx) => (
                <Item
                  key={`${idx}-${cIdx}`}
                  title={child.title} to={child.to} icon={child.icon}
                  selected={selectedModule} setSelected={setSelected}
                  broken={broken} toggleSidebar={toggleSidebar} collapsed={collapsed}
                  isSubmenuItem onSubMenuItemClick={() => setOpenSubMenus({})}
                  isTourHighlighted={isTourHighlighted(child.tourKey)}
                  registerTourRef={child.tourKey ? (el) => { itemRefs.current[child.tourKey!] = el; } : undefined}
                  disabled={tourActive}
                />
              ))}
            </SubMenu>
          </Box>
        </CustomTooltip>
      );
    });

  const onClosePopup = () => setShowInfoPopup({ message: "", show: false });
  const onShowPopup = () => setShowInfoPopup({ message: "You are about to sign out. Do you want to continue?", show: true });

  const handleLogout = () => {
    dispatch(logout());
    dispatch(resetNotificationData());
    removeItemFromSessionStorage("activeActionButtonId");
    removeItemFromSessionStorage("selections");
    localStorage.setItem("logout", Date.now().toString());
    navigate("/");
  };

  const collapseAllMenus = () => setOpenSubMenus({});
  const hasExpandedMenu = Object.values(openSubMenus).some(Boolean);

  const isRestrictedUser = userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU;

  return (
    <Box
      ref={sidebarBoxRef}
      className="custom-scroll"
      sx={{
        position: "sticky",
        display: "flex",
        top: 0,
        bottom: 0,
        zIndex: 1200,
        height: tourActive && broken ? `calc(100vh - ${MOBILE_STEPPER_HEIGHT}px)` : "100vh",
        borderRight: theme.palette.mode === "light" ? `1px solid ${colors.grey[700]}` : "none",
      }}
    >
      <Sidebar
        breakPoint={tourActive ? "xs" : "md"}
        rtl={sidebarRTL}
        backgroundColor={theme.palette.mode === "light" ? "#ffffff": colors.grey[800]}
        rootStyles={{ border: "none" }}
        image={sidebarImage}
      >
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
           <Menu
          key={menuKey}
          menuItemStyles={sidebarMenuItemStyles}
        >
            <Box sx={{ height: 2 }} />
            {collapsed && (
              <MenuItem
                icon={<MenuOutlined onClick={() => !tourActive && collapseSidebar()} />}
                style={{ margin: "10px 0 10px 0", color: colors.grey[100] }}
              />
            )}
            {!collapsed && (
              <Box mb={1.5} position="relative">
                <Box
                  display="flex" justifyContent="center" alignItems="center"
                  sx={{ cursor: "pointer" }}
                  onClick={() => {
                    const first = sideBarItems.find((l) => isTokenValueAvailable(l.token));
                    if (first) { setSelected(first.title); navigate(first.to); }
                  }}
                >
                  <img
                    alt="logo"
                    style={{ height: "60px", objectFit: "contain" }}
                    src={
                      theme.palette.mode === "dark"
                        ? branding?.logoIconWhite
                        : branding?.logoIcon || require("../../../branding-assets/v3biomed/logo-icon.png")
                    }
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={broken ? () => toggleSidebar() : () => !tourActive && collapseSidebar()}
                  sx={{ position: "absolute", right: 8, top: "30%", transform: "translateY(-50%)", color: colors.grey[100] }}
                >
                  <CloseOutlined fontSize="small" />
                </IconButton>
                {hasExpandedMenu && (
                  <IconButton
                    size="small" onClick={collapseAllMenus}
                    sx={{ position: "absolute", right: 40, top: "50%", transform: "translateY(-50%)", color: colors.grey[300] }}
                  >
                    <ListChevronsDownUp size={18} />
                  </IconButton>
                )}
              </Box>
            )}

            {SHOW_SIDEBAR_SEARCH && !collapsed && (
              <Box px="5%" pb={1.5}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    px: 1.6,
                    py: 0.85,
                    borderRadius: 2,
                    border: `1.5px solid ${alpha("#14b8a6", search ? 0.7 : 0.45)}`,
                    transition: "all 0.2s ease",
                    "&:focus-within": {
                      borderColor: alpha("#14b8a6", 0.75),
                      backgroundColor: alpha(colors.grey[900] ?? "#0d1117", 0.75),
                      boxShadow: `0 0 0 3px ${alpha("#14b8a6", 0.10)}, 0 0 12px ${alpha("#14b8a6", 0.12)}`,
                    },
                  }}
                >
                  <SearchIcon sx={{ fontSize: 15, color: search ? "#14b8a6" : "#4b5563", flexShrink: 0, transition: "color 0.2s" }} />
                  <InputBase
                    inputRef={searchRef}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchKeyDown}
                    placeholder="Search menu..."
                    sx={{
                      flex: 1,
                      fontSize: "0.76rem",
                      color: colors.grey[100],
                      "& input": {
                        padding: 0,
                        "&::placeholder": { color: "#4b5563", opacity: 1, fontSize: "0.76rem" },
                      },
                    }}
                  />
                  {search && (
                    <IconButton
                      size="small"
                      onClick={() => { setSearch(""); searchRef.current?.focus(); }}
                      sx={{
                        p: 0, width: 16, height: 16, color: "#4b5563",
                        "&:hover": { color: "#94a3b8", backgroundColor: "transparent" },
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            )}
          </Menu>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", paddingLeft: "3%", paddingRight: "3%" }} className="custom-scroll">
            <Menu key={menuKey} menuItemStyles={sidebarMenuItemStyles}>
              {search.trim() ? renderSearchResults() : renderTree()}
            </Menu>
          </Box>

          <Box sx={{ borderTop: `1px solid ${colors.grey[700]}`, paddingLeft: "3%", paddingRight: "3%", paddingTop: "8px" }}>
            <Menu menuItemStyles={sidebarMenuItemStyles}>
              {!search.trim() && (
                <>
                  {bottomItems.map((b, i) =>
                    isTokenValueAvailable(b.token) ? (
                      <Item
                        key={`bottom-${i}`}
                        title={b.title} to={b.to} icon={b.icon}
                        selected={selectedModule} setSelected={setSelected}
                        broken={broken} toggleSidebar={toggleSidebar} collapsed={collapsed}
                        isTourHighlighted={isTourHighlighted(b.tourKey)}
                        registerTourRef={b.tourKey ? (el) => { itemRefs.current[b.tourKey!] = el; } : undefined}
                        disabled={tourActive}
                      />
                    ) : null
                  )}

                  <CustomTooltip title={collapsed ? "Sign Out" : ""} placement="right" arrow>
                    <Box
                      ref={(el: HTMLDivElement | null) => { itemRefs.current["sign_out"] = el; }}
                      sx={{
                        borderRadius: "8px",
                        border: isTourHighlighted("sign_out") ? "1px solid rgba(20,184,166,0.8)" : "1px solid transparent",
                        backgroundColor: isTourHighlighted("sign_out") ? alpha("#14b8a6", 0.12) : "transparent",
                        position: "relative",
                        zIndex: isTourHighlighted("sign_out") ? 1450 : "auto",
                        transition: "border-color 0.25s, background-color 0.25s",
                        mx: isTourHighlighted("sign_out") ? "-2px" : 0,
                        px: isTourHighlighted("sign_out") ? "2px" : 0,
                      }}
                    >
                      <MenuItem icon={<LogOut size={18} color={colors.error[300]} />} onClick={onShowPopup} style={{ borderRadius: 10 }}>
                        <Typography style={{ fontWeight: 600, fontSize: "14px", color: "#ff4d4f" }}>Sign Out</Typography>
                      </MenuItem>
                    </Box>
                  </CustomTooltip>
                </>
              )}
            </Menu>

            <CustomTooltip title={collapsed ? "Take a Tour" : ""} placement="right" arrow>
              <Box px={1} pt={1} pb={0.5}>
                <Button
                  fullWidth
                  size="small"
                  onClick={startTour}
                  variant="outlined"
                  sx={{
                    textTransform: "none",
                    fontSize: "0.78rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    borderColor: alpha("#14b8a6", 0.4),
                    color: "#14b8a6",
                    py: 0.6,
                    "&:hover": { borderColor: "#14b8a6", backgroundColor: alpha("#14b8a6", 0.08) },
                    ...(collapsed && { minWidth: 0, px: 0.5 }),
                  }}
                >
                  {collapsed ? <Explore fontSize="small" /> : "Take a Tour"}
                </Button>
              </Box>
            </CustomTooltip>
          </Box>
        </Box>
      </Sidebar>

      {tourActive && broken && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1199,
            pointerEvents: "all",
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )}

      {tourActive && currentTourStep && (
        <>
          <SpotlightOverlay activeEl={itemRefs.current[currentTourStep.key] ?? null} />
          <Box
            sx={{
              position: "fixed",
              zIndex: 1449,
              pointerEvents: "none",
              display: "flex",
              flexDirection: "column",
              ...(broken
                ? {
                  left: 0,
                  right: 0,
                  px: 2,
                  py: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: colors.grey[800],
                  borderTop: `1px solid ${colors.grey[700]}`,
                }
                : {
                  left: sidebarRight,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  px: 4,
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "transparent",
                }),
            }}
          >
            <Box
              component="img"
              src={
                theme.palette.mode === "dark"
                  ? branding?.logoIconWhite
                  : branding?.logoIcon || require("../../../branding-assets/v3biomed/logo-icon.png")
              }
              alt="Logo"
              sx={{ height: isMobile ? 30 : 56, objectFit: "contain", mb: isMobile ? 0 : 5 }}
            />

            <Box sx={{ width: "100%", maxWidth: broken ? "100%" : 620, mb: broken ? 0.5 : 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                  {isRestrictedUser ? "Step 3 of 3" : "Step 1 of 1"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#14b8a6", fontWeight: 600 }}>
                  {isRestrictedUser ? "60%" : "100%"}
                </Typography>
              </Box>
              <Box sx={{ height: broken ? 4 : 6, borderRadius: 3, backgroundColor: alpha("#14b8a6", 0.15), overflow: "hidden" }}>
                <Box sx={{
                  height: "100%",
                  width: isRestrictedUser ? "60%" : "100%",
                  backgroundColor: "#14b8a6",
                  borderRadius: 3,
                }} />
              </Box>
            </Box>

            <Box sx={{ width: "100%", maxWidth: broken ? "100%" : 620 }}>
              <Typography
                variant={broken ? "body2" : "h4"}
                fontWeight={600}
                sx={{
                  color: alpha("#9ca3af", 0.8),
                  display: "block",
                  textAlign: broken ? "left" : "center",
                }}
              >
                {isRestrictedUser ? "Step 3: Platform Tour" : "Welcome Back!"}
              </Typography>
              <Typography sx={{ color: "#4b5563", mt: 1, textAlign: "center" }}>
                {isRestrictedUser
                  ? "Let us show you around the platform. Follow the highlights to learn about each section."
                  : "Take a quick refresher tour of the platform to discover new features and updates since your last visit."}
              </Typography>
            </Box>
          </Box>
          <Box
            ref={tooltipRef}
            key={animKey}
            sx={{
              position: "fixed",
              left: isMobile ? 50 : sidebarRight + TOOLTIP_GAP,
              top: isMobile ? tooltipTop + 50 : tooltipTop,
              width: TOOLTIP_WIDTH,
              zIndex: 1500,
              pointerEvents: tooltipAnim === "exit" ? "none" : "auto",
              animation: tooltipAnim === "enter"
                ? `${tooltipEnter} 0.3s cubic-bezier(0.22,1,0.36,1) forwards`
                : `${tooltipExit} 0.18s ease forwards`,
              transition: "top 0.35s cubic-bezier(0.22,1,0.36,1)",
            }}
          >
            <Box sx={{
              backgroundColor: theme.palette.mode === "dark" ? "#1f2933" : "#fff",
              borderRadius: 3,
              boxShadow: "0 16px 48px rgba(0,0,0,0.3), 0 0 0 1px rgba(20,184,166,0.08)",
              p: 3,
              border: 1,
              borderColor: colors.grey[700],
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                <Typography variant="caption" sx={{ color: "#14b8a6", fontWeight: 700, letterSpacing: 0.5 }}>
                  {tourStep + 1} of {activeTourSteps.length}
                </Typography>
                <IconButton
                  size="small" onClick={handleTourSkip}
                  sx={{ color: "#64748b", width: 26, height: 26, "&:hover": { color: "#94a3b8", backgroundColor: alpha("#fff", 0.05) } }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              <Box sx={{ height: 3, borderRadius: 2, backgroundColor: alpha("#14b8a6", 0.15), mb: 2, overflow: "hidden" }}>
                <Box sx={{
                  height: "100%",
                  width: `${((tourStep + 1) / activeTourSteps.length) * 100}%`,
                  backgroundColor: "#14b8a6",
                  borderRadius: 2,
                  transition: "width 0.4s ease",
                }} />
              </Box>

              <Typography fontWeight={700} fontSize="1rem" sx={{ color: colors.grey[100], mb: 0.75 }}>
                {currentTourStep.title}
              </Typography>

              <Typography variant="body2" sx={{ color: "#94a3b8", mb: 2.5, lineHeight: 1.65 }}>
                {currentTourStep.description}
              </Typography>

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  size="small" variant="outlined"
                  startIcon={<ChevronLeftIcon sx={{ fontSize: 16 }} />}
                  disabled={tourStep === 0}
                  onClick={handleTourBack}
                  sx={{
                    textTransform: "none", borderColor: "#334155", color: "#cbd5e1",
                    borderRadius: 1.5, px: 1.5, fontWeight: 600, fontSize: "0.78rem",
                    "&:hover": { borderColor: "#14b8a6", backgroundColor: alpha("#14b8a6", 0.06), color: "#14b8a6" },
                    "&.Mui-disabled": { borderColor: "#1e293b", color: "#334155" },
                  }}
                >Back</Button>

                <Button
                  size="small" variant="text" onClick={handleTourSkip}
                  sx={{ textTransform: "none", color: "#64748b", fontSize: "0.78rem", "&:hover": { color: "#94a3b8", backgroundColor: "transparent" } }}
                >Skip Tour</Button>

                <Button
                  size="small" variant="contained"
                  endIcon={<ChevronRightIcon sx={{ fontSize: 16 }} />}
                  onClick={handleTourNext}
                  sx={{
                    textTransform: "none", backgroundColor: "#14b8a6", color: "#fff",
                    fontWeight: 600, borderRadius: 1.5, px: 2, fontSize: "0.78rem",
                    boxShadow: "0 4px 14px rgba(20,184,166,0.3)",
                    "&:hover": { backgroundColor: "#0d9488", boxShadow: "0 6px 20px rgba(20,184,166,0.4)" },
                  }}
                >
                  {tourStep === activeTourSteps.length - 1 ? "Finish" : "Next"}
                </Button>
              </Box>
            </Box>
          </Box>
        </>
      )}

      <OptionsPopup
        open={showInfoPopup.show}
        onClose={onClosePopup}
        variant="custom"
        message={showInfoPopup.message}
        title="Sign Out Confirmation"
        buttons={[
          { name: "Continue", color: "primary", onClick: handleLogout },
          { name: "Cancel", color: "secondary", onClick: onClosePopup },
        ]}
      />
    </Box>
  );
};

export default MyProSidebar;